import {
  EXPANDED_SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSE_BREAKPOINT,
} from "@/app-shell/navigation/sidebarDimensions"

interface GridConfig {
  CONTAINER_PADDING: [number, number]
  MARGIN: [number, number]
  ROW_HEIGHT: number
}

interface ScalingConfig {
  BASE_VIEWPORT_WIDTH: number
  ENABLE_PROPORTIONAL_SCALING: boolean
  MAX_CONTAINER_WIDTH: number
  MIN_CONTAINER_WIDTH: number
}

const COVI_DOCK_WIDTH_CSS_VAR = "--covi-dock-width"
const GRID_WRAPPER_HORIZONTAL_PADDING = 48
const MAIN_REGION_SELECTOR = "[data-app-shell-main-region]"

export function getScaledGridDimensions(
  gridConfig: GridConfig,
  scalingConfig: ScalingConfig,
  containerWidth?: number,
): {
  rowHeight: number
  margin: [number, number]
  containerPadding: [number, number]
} {
  if (!scalingConfig.ENABLE_PROPORTIONAL_SCALING) {
    return {
      rowHeight: gridConfig.ROW_HEIGHT,
      margin: gridConfig.MARGIN,
      containerPadding: gridConfig.CONTAINER_PADDING,
    }
  }

  const actualWidth = containerWidth || scalingConfig.BASE_VIEWPORT_WIDTH
  const scaleFactor = actualWidth / scalingConfig.BASE_VIEWPORT_WIDTH

  return {
    rowHeight: Math.max(20, gridConfig.ROW_HEIGHT * scaleFactor),
    margin: gridConfig.MARGIN,
    containerPadding: gridConfig.CONTAINER_PADDING,
  }
}

export function getResponsiveGridContainerWidth(
  scalingConfig: ScalingConfig,
): number {
  if (typeof window === "undefined") {
    return scalingConfig.BASE_VIEWPORT_WIDTH
  }

  const viewportWidth = document.documentElement.clientWidth
  const sidebarWidth = getDockedNavigationWidth(viewportWidth)
  const availableWidth =
    viewportWidth -
    sidebarWidth -
    getCoviDockWidth() -
    GRID_WRAPPER_HORIZONTAL_PADDING

  return Math.min(
    Math.max(availableWidth, scalingConfig.MIN_CONTAINER_WIDTH),
    scalingConfig.MAX_CONTAINER_WIDTH,
  )
}

/**
 * The width the navigation docked left of the content takes. Measured from
 * where the shell's main region starts, since the sidebar can be widened by a
 * module or hidden by the user; outside the shell, the default docked width
 * above the breakpoint and none at or below it, where the sidebar is an
 * overlay rather than a column.
 */
function getDockedNavigationWidth(viewportWidth: number): number {
  const mainRegion = document.querySelector(MAIN_REGION_SELECTOR)
  if (mainRegion) {
    return Math.max(0, mainRegion.getBoundingClientRect().left)
  }
  return viewportWidth <= SIDEBAR_COLLAPSE_BREAKPOINT
    ? 0
    : EXPANDED_SIDEBAR_WIDTH
}

function getCoviDockWidth(): number {
  const rawDockWidth = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(COVI_DOCK_WIDTH_CSS_VAR)
  const dockWidth = Number.parseFloat(rawDockWidth)

  return Number.isFinite(dockWidth) && dockWidth > 0 ? dockWidth : 0
}
